import { createPortal } from "react-dom";
import { Trash2 } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { readDraft, writeDraft } from "./draft-storage";
type Mark = { region: string; start: number; end: number };
function textNodes(root: Element) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode as Text);
  return nodes;
}
export function TextHighlights({ storageKey, enabled, children }: { storageKey: string; enabled: boolean; children: ReactNode }) {
  const root = useRef<HTMLDivElement>(null);
  const [marks, setMarks] = useState<Mark[]>([]);
  const [warning, setWarning] = useState("");
  const [menu, setMenu] = useState<{ mark: Mark; x: number; y: number } | null>(null);
  const renderedMarks = useRef<{ mark: Mark; range: Range }[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const dismiss = (event: PointerEvent) => { if (!menuRef.current?.contains(event.target as Node)) setMenu(null); };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") setMenu(null); };
    const close = () => setMenu(null);
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [menu]);
  const ready = useRef(false);
  const suppressClick = useRef(false);
  const writes = useRef(Promise.resolve());
  useEffect(() => {
    let active = true;
    void readDraft<Mark[]>(storageKey).then((saved) => { if (active) { setMarks(saved ?? []); ready.current = true; } }).catch(() => { if (active) { ready.current = true; setWarning("Highlights could not be restored on this device."); } });
    return () => { active = false; };
  }, [storageKey]);
  function save(next: Mark[]) {
    setMarks(next);
    writes.current = writes.current.then(() => writeDraft(storageKey, next)).catch(() => setWarning("Highlights could not be saved on this device."));
  }
  function capture() {
    if (!enabled || !ready.current) return false;
    if (!globalThis.CSS?.highlights || typeof Highlight === "undefined") { setWarning("Text highlighting is unavailable in this browser."); return false; }
    const selection = window.getSelection();
    if (!selection?.rangeCount || selection.isCollapsed) return false;
    const range = selection.getRangeAt(0);
    const element = range.startContainer.nodeType === Node.ELEMENT_NODE ? range.startContainer as Element : range.startContainer.parentElement;
    const region = element?.closest<HTMLElement>("[data-highlight-region]");
    if (!region || !root.current?.contains(region) || !region.contains(range.endContainer)) return false;
    const before = range.cloneRange(); before.selectNodeContents(region); before.setEnd(range.startContainer, range.startOffset);
    const mark = { region: region.dataset.highlightRegion!, start: before.toString().length, end: before.toString().length + range.toString().length };
    if (mark.end <= mark.start) return false;
    const overlapping = marks.filter((m) => m.region === mark.region && m.start <= mark.end && m.end >= mark.start);
    const next = { region: mark.region, start: Math.min(mark.start, ...overlapping.map(m => m.start)), end: Math.max(mark.end, ...overlapping.map(m => m.end)) };
    save([...marks.filter(m => !overlapping.includes(m)), next].slice(-100));
    selection.removeAllRanges();
    return true;
  }
  useEffect(() => {
    if (!root.current || !globalThis.CSS?.highlights || typeof Highlight === "undefined") return;
    const ranges: Range[] = [];
    renderedMarks.current = [];
    for (const mark of marks) {
      const region = [...root.current.querySelectorAll<HTMLElement>("[data-highlight-region]")].find(e => e.dataset.highlightRegion === mark.region);
      if (!region) continue;
      let offset = 0; const range = document.createRange(); let start = false;
      for (const node of textNodes(region)) {
        const end = offset + node.length;
        if (!start && mark.start >= offset && mark.start < end) { range.setStart(node, mark.start - offset); start = true; }
        if (start && mark.end <= end) { range.setEnd(node, mark.end - offset); ranges.push(range); renderedMarks.current.push({ mark, range }); break; }
        offset = end;
      }
    }
    CSS.highlights.set("sat-text", new Highlight(...ranges));
    return () => { CSS.highlights.delete("sat-text"); };
  }, [marks, children]);
  return <div ref={root} className={`highlight-surface${enabled ? " highlight-mode" : ""}`}
    onPointerDown={() => { suppressClick.current = false; }}
    onPointerUp={() => { suppressClick.current = capture(); }}
    onKeyUp={() => { capture(); }}
    onClickCapture={(event) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      if (suppressClick.current) {
        event.preventDefault(); event.stopPropagation(); suppressClick.current = false; return;
      }
      const hit = renderedMarks.current.find(({ range }) => [...range.getClientRects()].some(rect =>
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom));
      if (hit && event.detail !== 0) {
        event.preventDefault(); event.stopPropagation();
        setMenu({ mark: hit.mark, x: Math.max(12, Math.min(event.clientX - 80, window.innerWidth - 184)), y: Math.max(12, Math.min(event.clientY - 56, window.innerHeight - 60)) });
      } else if (enabled && (event.target as Element).closest(".test-choice")) {
        event.preventDefault(); event.stopPropagation();
      }
    }}>
    {warning && <span className="highlight-warning" role="status">{warning}</span>}{children}
    {menu && createPortal(<div ref={menuRef} className="highlight-menu" role="dialog" aria-label="Highlight actions" style={{ left: menu.x, top: menu.y }}>
      <button type="button" autoFocus onClick={() => { save(marks.filter(mark => mark !== menu.mark)); setMenu(null); }}><Trash2 size={16} />Delete highlight</button>
    </div>, document.body)}
  </div>;
}
